import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { RequalificationRefusedNextStep } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useFlags } from '@/services/auth';

export const QuoteRefusalDialog = ({
  isOpen,
  onClose,
  onRefuse,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onRefuse: (reason: string, nextStep: RequalificationRefusedNextStep) => Promise<void>;
  isLoading: boolean;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({
        id: 'article.actions.accept_requalification.refuse.modal.title',
        message: 'Refuse quote',
      })}
      isOpen={isOpen}
      onOpenChange={onClose}
    >
      <RefusalDialogForm onCancel={onClose} onRefuse={onRefuse} isLoadingRefuse={isLoading} />
    </Dialog>
  );
};

const RefusalDialogForm = ({
  onCancel,
  onRefuse,
  isLoadingRefuse,
}: {
  onCancel: () => void;
  onRefuse: (reason: string, nextStep: RequalificationRefusedNextStep) => Promise<void>;
  isLoadingRefuse: boolean;
}) => {
  const { t } = useLingui();
  const { flags } = useFlags();
  const { article } = useArticleContext();
  const { workflow } = useRequestContext();

  const [reason, setReason] = useState('');
  const [nextStep, setNextStep] = useState<RequalificationRefusedNextStep | null>(null);
  const [showError, setShowError] = useState(false);

  const isEnableQuoteRefusalOptionsEnabled = flags['enable-quote-refusal-options'];
  const missingReason = !reason;
  const missingNextStep = isEnableQuoteRefusalOptionsEnabled && !nextStep;

  const handleRefuse = () => {
    setShowError(true);

    if (missingReason || missingNextStep) {
      return;
    }

    onRefuse(reason, nextStep ?? 'new-diagnosis');
  };

  const currentNode = workflow?.nodes.find((node) => node.id === article.step?.id);

  if (!currentNode || !('step' in currentNode) || currentNode.step !== 'analysis') {
    return null;
  }

  const showReturnAsIsOption = !!currentNode.next.requalificationRefusedReturnAsIs;

  return (
    <>
      <main>
        <Stack gap="1rem">
          <TextArea
            label={t({
              id: 'article.actions.accept_requalification.refuse.modal.reason.label',
              message: 'Specify the reason for refusal',
            })}
            placeholder={t({
              id: 'article.actions.accept_requalification.refuse.modal.reason.placeholder',
              message: 'Describe the reason...',
            })}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={
              showError && missingReason
                ? t({
                    id: 'article.actions.accept_requalification.refuse.modal.reason.error',
                    message: 'Please provide a reason',
                  })
                : undefined
            }
          />

          {isEnableQuoteRefusalOptionsEnabled && (
            <Label
              label={t({
                id: 'article.actions.accept_requalification.refuse.modal.next-step.label',
                message: 'What would you like to do next?',
              })}
            >
              <RadioBoxGroup
                name="quote-refusal-next-step"
                ariaLabel={t({
                  id: 'article.actions.accept_requalification.refuse.modal.next-step.label',
                  message: 'What would you like to do next?',
                })}
                direction="column"
                value={nextStep}
                onChange={(newNextStep) => setNextStep(newNextStep)}
                isInvalid={showError && !nextStep}
              >
                <RadioBox
                  value="new-analysis"
                  title={t({
                    id: 'article.actions.accept_requalification.refuse.modal.next-step.new-analysis.title',
                    message: 'Ask for a new quote from the same workshop',
                  })}
                  ariaLabel={t({
                    id: 'article.actions.accept_requalification.refuse.modal.next-step.new-analysis.title',
                    message: 'Ask for a new quote from the same workshop',
                  })}
                >
                  <Paragraph size="200" color="secondary">
                    <Trans id="article.actions.accept_requalification.refuse.modal.next-step.new-analysis.description">
                      Request a revised quote based on your feedback
                    </Trans>
                  </Paragraph>
                </RadioBox>

                {showReturnAsIsOption && (
                  <RadioBox
                    value="return-as-is"
                    title={t({
                      id: 'article.actions.accept_requalification.refuse.modal.next-step.return-as-is.title',
                      message: 'Return as is',
                    })}
                    ariaLabel={t({
                      id: 'article.actions.accept_requalification.refuse.modal.next-step.return-as-is.title',
                      message: 'Return as is',
                    })}
                  >
                    <Paragraph size="200" color="secondary">
                      <Trans id="article.actions.accept_requalification.refuse.modal.next-step.return-as-is.description">
                        The item will be returned to the customer without any service
                      </Trans>
                    </Paragraph>
                  </RadioBox>
                )}

                <RadioBox
                  value="new-diagnosis"
                  title={t({
                    id: 'article.actions.accept_requalification.refuse.modal.next-step.new-diagnosis.title',
                    message: 'Do a new diagnosis',
                  })}
                  ariaLabel={t({
                    id: 'article.actions.accept_requalification.refuse.modal.next-step.new-diagnosis.title',
                    message: 'Do a new diagnosis',
                  })}
                >
                  <Paragraph size="200" color="secondary">
                    <Trans id="article.actions.accept_requalification.refuse.modal.next-step.new-diagnosis.description">
                      Update the services or send the item to another workshop
                    </Trans>
                  </Paragraph>
                </RadioBox>
              </RadioBoxGroup>

              {showError && !nextStep && (
                <Message type="error">
                  <Trans id="article.actions.accept_requalification.refuse.modal.next-step.error">
                    Please select what you want to do next
                  </Trans>
                </Message>
              )}
            </Label>
          )}
        </Stack>
      </main>

      <Footer>
        <FooterButtons>
          <Button variant="secondary" onPress={onCancel}>
            <Trans id="article.actions.accept_requalification.refuse.modal.cancel">Cancel</Trans>
          </Button>
          <Button variant="danger" onPress={handleRefuse} isLoading={isLoadingRefuse}>
            <Trans id="article.actions.accept_requalification.refuse.modal.refuse">
              Refuse quote
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
